import { h } from 'preact';
import { Text } from 'preact-i18n';

import Avatar from '../gui/avatar';

// Stevne invitations on the detail view: the ones to me (answer buttons until answered)
// and the ones I sent (who has answered what). Pure props → markup; the store lives in detail.js.
const ANSWERS = [
    {
        status: 'going', id: 'terminliste.invite.answerGoing', text: 'Going', icon: 'fa-solid fa-check', active: 'btn-success',
    },
    {
        status: 'maybe', id: 'terminliste.invite.answerMaybe', text: 'Maybe', icon: 'fa-solid fa-question', active: 'btn-info',
    },
    {
        status: 'declined', id: 'terminliste.invite.answerDeclined', text: 'Can\'t make it', icon: 'fa-solid fa-xmark', active: 'btn-secondary',
    },
];

const CHIP = {
    going: { cls: 'text-bg-success', icon: 'fa-solid fa-check', id: 'terminliste.invite.answerGoing', text: 'Going' },
    maybe: { cls: 'text-bg-info', icon: 'fa-solid fa-question', id: 'terminliste.invite.answerMaybe', text: 'Maybe' },
    declined: { cls: 'text-bg-secondary', icon: 'fa-solid fa-xmark', id: 'terminliste.invite.answerDeclined', text: 'Can\'t make it' },
    sent: { cls: 'bg-body-tertiary text-body-secondary border', icon: 'fa-regular fa-clock', id: 'terminliste.invite.waitingShort', text: 'Waiting' },
};

export const ANSWER_ORDER = ['going', 'maybe', 'declined', 'sent'];

/** { going, maybe, declined, sent } counts over the invitations I sent. */
export function countAnswers(sent = []) {
    const counts = { going: 0, maybe: 0, declined: 0, sent: 0 };
    (sent || []).forEach(invite => {
        const key = CHIP[invite.status] ? invite.status : 'sent';
        counts[key] += 1;
    });
    return counts;
}

/** «3 skal · 1 kanskje · 2 venter» — only the non-zero groups. */
export function InviteSummary({ sent = [] }) {
    const counts = countAnswers(sent);
    const parts = ANSWER_ORDER.filter(key => counts[key] > 0);
    if (parts.length === 0) {
        return null;
    }
    return (
        <span class='nsf-invite-summary'>
            {parts.map((key, idx) => (
                <span key={key} class='text-nowrap' data-count={key}>
                    {idx > 0 ? ' · ' : ''}
                    {counts[key]} <Text id={CHIP[key].id}>{CHIP[key].text}</Text>
                </span>
            ))}
        </span>
    );
}

/** One invited member: avatar, name and a coloured answer chip. */
export function InviteChip({ invite, size = 22 }) {
    const meta = CHIP[invite.status] || CHIP.sent;
    return (
        <span
            class={`badge rounded-pill ${meta.cls} d-inline-flex align-items-center gap-1 fw-normal nsf-invite-chip`}
            data-invite-id={invite.id}
            data-status={invite.status}
            style='font-size: .8em;'
        >
            {size > 0 && <Avatar name={invite.to.name} image={invite.to.image} faceBox={invite.to.faceBox} size={size} />}
            {invite.to.name}
            <i class={`${meta.icon} ${invite.to.name ? 'ms-1' : ''}`} title={meta.text} />
        </span>
    );
}

export function InviteStatus({ status }) {
    const meta = ANSWERS.find(a => a.status === status);
    if (!meta) {
        return <span class='text-body-secondary'><Text id='terminliste.invite.waiting'>waiting for an answer</Text></span>;
    }
    return <span><i class={`${meta.icon} me-1`} /><Text id={meta.id}>{meta.text}</Text></span>;
}

/** Invitations to the viewer. Unanswered ones get the three answer buttons. */
export function InvitesReceived({ received = [], onAnswer }) {
    if (!received || received.length === 0) {
        return null;
    }
    return (
        <div class='nsf-invites-received'>
            {received.map(invite => (
                <div key={invite.id} class={`alert ${invite.status === 'sent' ? 'alert-info' : 'alert-light border'} py-2 px-3 mb-2`} data-invite-id={invite.id}>
                    <div class='d-flex align-items-center gap-2'>
                        <Avatar name={invite.from.name} image={invite.from.image} faceBox={invite.from.faceBox} size={28} />
                        <div class='flex-grow-1 small'>
                            <div class='fw-semibold'>
                                <Text id='terminliste.invite.received' fields={{ name: invite.from.name }}>{invite.from.name} invites you</Text>
                            </div>
                            {invite.message && <div class='fst-italic'>«{invite.message}»</div>}
                            {invite.status !== 'sent' && (
                                <div class='text-body-secondary'>
                                    <Text id='terminliste.invite.youAnswered'>You answered</Text>: <InviteStatus status={invite.status} />
                                </div>
                            )}
                        </div>
                    </div>
                    {invite.status === 'sent' && (
                        <div class='btn-group btn-group-sm mt-2' role='group'>
                            {ANSWERS.map(meta => (
                                <button
                                    key={meta.status}
                                    type='button'
                                    class={`btn btn-outline-${meta.active.replace('btn-', '')} text-nowrap`}
                                    data-answer={meta.status}
                                    onClick={onAnswer ? () => onAnswer(invite, meta.status) : undefined}
                                >
                                    <i class={`${meta.icon} me-1`} />
                                    <Text id={meta.id}>{meta.text}</Text>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

/** Invitations the viewer sent: summary line and one chip per member, answered first. */
export function InvitesSent({ sent = [], onRemind, reminding = false }) {
    if (!sent || sent.length === 0) {
        return null;
    }
    const ordered = [...sent].sort((a, b) => ANSWER_ORDER.indexOf(CHIP[a.status] ? a.status : 'sent') - ANSWER_ORDER.indexOf(CHIP[b.status] ? b.status : 'sent'));
    const waiting = countAnswers(sent).sent;
    return (
        <div class='nsf-invites-sent small mb-2'>
            <div class='d-flex flex-wrap align-items-center gap-2'>
                <span class='fw-semibold'><Text id='terminliste.invite.sentTo'>You invited</Text>:</span>
                <InviteSummary sent={sent} />
                {onRemind && waiting > 0 && (
                    <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill py-0 nsf-invite-remind' disabled={reminding} onClick={onRemind}>
                        <i class={`fa-solid ${reminding ? 'fa-spinner fa-spin' : 'fa-bell'} me-1`} />
                        <Text id='terminliste.invite.remind' fields={{ count: waiting }}>Remind {waiting} waiting</Text>
                    </button>
                )}
            </div>
            <div class='d-flex flex-wrap gap-1 mt-1'>
                {ordered.map(invite => <InviteChip key={invite.id} invite={invite} />)}
            </div>
        </div>
    );
}

export default InvitesReceived;
